import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sessionAPI } from '../services/api';
import { useReportPdf, ReportPdfActions, ReportPdfBody } from '../components/reports/ReportPdf';
import { hasReport } from './position/derive';
import { useReportsHeader } from '../context/ReportsHeaderContext';
import { USE_DEMO_DATA } from '../data/demoMode';
import { DUMMY_SESSIONS, withCurrentRecruiterInDemoSessions } from '../data/dashboardDummyData';
import './AdminDashboard.css';
import './Reports.css';

const C = {
  brand: '#6366F1',
  pass: '#10B981',
  below: '#EF4444',
  t2: '#475569',
  t3: '#64748B',
  t4: '#94A3B8',
};

const STATUS_LABELS = {
  completed: 'Completed',
  incomplete: 'Incomplete',
  in_progress: 'In progress',
  scheduled: 'Scheduled',
  terminated: 'Terminated',
  abandoned: 'Abandoned',
};

const statusLabel = (status) =>
  STATUS_LABELS[String(status || '').toLowerCase()] || (status ? String(status).replace(/_/g, ' ') : '—');

/** The Interview Report's 0–100 headline score; older APIs without it: overall_score (0–10) × 10 (position/derive.resultOf). */
function reportScore(session) {
  if (!hasReport(session)) return null;
  const raw = session.report_score ?? (session.overall_score != null ? Number(session.overall_score) * 10 : null);
  return raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
}

/** Hire bar of the session's batch, 0–100 (API min_hire_score, app/services/eval_settings.py). */
function hireBarOf(session) {
  const n = Number(session.min_hire_score);
  return Number.isFinite(n) ? n : 50;
}

/** Right panel: the WizeTok Interview Report PDF, the same document the position Report modal shows. */
function SessionReport({ session }) {
  const sessionId = session.id ?? session.session_id;
  const report = useReportPdf({ sessionId });
  const name = session.candidate_name ?? session.name ?? 'Candidate';
  const date = session.created_at ? new Date(session.created_at).toLocaleDateString('en-GB') : session.date;
  const meta = [session.role ?? session.job_role, session.campaign_name ?? session.campaign, date].filter(Boolean).join(' · ');

  return (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'white' }}>
      <div className="wz-report-head">
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{name}</span>
          <span className="wz-sub wz-ellipsis" style={{ fontSize: 12 }}>{meta}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <ReportPdfActions {...report} />
        </div>
      </div>
      <ReportPdfBody pdf={report.pdf} title={`Interview report · ${name}`} />
    </div>
  );
}

function Placeholder({ title, text }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
      <div style={{ textAlign: 'center', color: C.t4, maxWidth: 360, padding: 24 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.t2 }}>{title}</div>
        {text && <div style={{ fontSize: 12, marginTop: 6 }}>{text}</div>}
      </div>
    </div>
  );
}

const Reports = () => {
  const { setReportsSearchApi, searchQuery } = useReportsHeader();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(() => searchParams.get('sessionId') || null);
  const [examSetFilter, setExamSetFilter] = useState(() => searchParams.get('exam_set_id') || null);

  useEffect(() => {
    loadSessions(examSetFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSessions = async (examSetId) => {
    try {
      setLoading(true);
      if (USE_DEMO_DATA) {
        // Same shared sessions as Dashboard Recommendation funnel
        setSessions(withCurrentRecruiterInDemoSessions(DUMMY_SESSIONS));
        return;
      }
      const response = await sessionAPI.getAllSessions(examSetId ? { exam_set_id: examSetId } : undefined);
      setSessions(response.data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      alert('Failed to load interview sessions');
    } finally {
      setLoading(false);
    }
  };

  const examSetFilterName = useMemo(() => {
    if (!examSetFilter) return null;
    const match = (sessions ?? []).find((s) => s.exam_set_id === examSetFilter);
    return match?.exam_set_name || 'this exam set';
  }, [sessions, examSetFilter]);

  const clearExamSetFilter = () => {
    setExamSetFilter(null);
    loadSessions(null);
  };

  const selectedSessionData =
    (sessions ?? []).find((s) => String(s.id ?? s.session_id) === String(selectedSession)) || null;

  const sessionsForList = useMemo(() => {
    const list = sessions ?? [];
    const seen = new Set();
    const out = [];
    for (const s of list) {
      const id = s.id ?? s.session_id;
      if (id != null && id !== '') {
        const key = String(id);
        if (seen.has(key)) continue;
        seen.add(key);
      }
      out.push(s);
    }
    return out;
  }, [sessions]);

  const filteredSessionsForList = useMemo(() => {
    const q = (searchQuery ?? '').trim().toLowerCase();
    if (!q) return sessionsForList;
    return sessionsForList.filter((s) => {
      const name = (s.candidate_name ?? s.name ?? '').toLowerCase();
      const email = (s.candidate_email ?? s.email ?? '').toLowerCase();
      const role = (s.job_role ?? s.role ?? s.position ?? '').toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [sessionsForList, searchQuery]);

  useEffect(() => {
    setReportsSearchApi({
      sessions: sessionsForList,
      selectSession: (sessionId) => setSelectedSession(sessionId),
    });
    return () => setReportsSearchApi(null);
  }, [sessionsForList, setReportsSearchApi]);

  let rightPanel;
  if (!selectedSessionData) {
    rightPanel = <Placeholder title="Select a session to view the report" />;
  } else if (!hasReport(selectedSessionData)) {
    rightPanel = (
      <Placeholder
        title={`${selectedSessionData.candidate_name ?? selectedSessionData.name ?? 'This candidate'} · ${statusLabel(selectedSessionData.status)}`}
        text="The report is available once the interview has ended."
      />
    );
  } else {
    rightPanel = <SessionReport key={String(selectedSession)} session={selectedSessionData} />;
  }

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
      {/* LEFT: Session list panel */}
      <div style={{ width: 248, flexShrink: 0, borderRight: "1px solid #E2E8F0", background: "white", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F8FAFC" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#0F172A" }}>Sessions</span>
          <span style={{ fontSize: 10, color: C.t4 }}>
            {searchQuery.trim()
              ? `${filteredSessionsForList.length} / ${(sessions ?? []).length}`
              : `${(sessions ?? []).length} candidates`}
          </span>
        </div>
        {examSetFilter && (
          <div style={{ padding: "8px 14px", borderBottom: "1px solid #E2E8F0", background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 11, color: "#4338CA", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Filtered: {examSetFilterName}
            </span>
            <button
              type="button"
              onClick={clearExamSetFilter}
              style={{ fontSize: 10, fontWeight: 600, color: "#4338CA", background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}
            >
              Clear ✕
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading-container" style={{ flex: 1 }}>
            <div className="spinner"></div>
            <p>Loading sessions...</p>
          </div>
        ) : (
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filteredSessionsForList.length === 0 && searchQuery.trim() ? (
              <div style={{ padding: '16px 14px', fontSize: 12, color: C.t3 }}>
                No candidates match your search.
              </div>
            ) : null}
            {filteredSessionsForList.map((session) => {
              const sid = session.id ?? session.session_id;
              const isActive = sid != null && String(sid) === String(selectedSession);
              const name = session.candidate_name ?? session.name ?? "Unknown";
              const role = session.role ?? session.job_role ?? session.position ?? "";
              const score = reportScore(session);
              const date = session.created_at
                ? new Date(session.created_at).toLocaleDateString("en-GB")
                : (session.date ?? "—");
              const finished = hasReport(session);

              return (
                <div
                  key={sid ?? `${name}-${session.created_at}`}
                  onClick={() => sid != null && setSelectedSession(sid)}
                  style={{
                    padding: "12px 14px", borderBottom: "1px solid #E2E8F0", cursor: sid != null ? "pointer" : "default",
                    background: isActive ? "rgba(99,102,241,.06)" : "none",
                    borderLeft: "3px solid " + (isActive ? C.brand : "transparent"),
                    transition: "all .15s",
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F8FAFC"; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "none"; }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 6 }}>
                      {name}
                    </div>
                    <span style={{
                      fontSize: 9, padding: "2px 7px", borderRadius: 20, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
                      background: finished ? "#F1F5F9" : "#FEF3C7", color: finished ? C.t2 : "#92400E",
                    }}>
                      {statusLabel(session.status)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: C.t2, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {role}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: score == null ? C.t4 : score >= hireBarOf(session) ? C.pass : C.below }}>
                      {score != null ? Math.round(score) : (session.cefr_band || "—")}
                      {score != null && <span style={{ fontSize: 10, color: C.t4, fontWeight: 400 }}> /100</span>}
                    </span>
                    <span style={{ fontSize: 9, color: C.t4 }}>{date}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RIGHT: the Interview Report */}
      {rightPanel}
    </div>
  );
};

export default Reports;
